import React from "react";
import { Zap, Gift, Lock } from "lucide-react";
import {
  freeTrialBlurb,
  freeScanUsedPhrase,
  starterScansPhrase,
  planNumbers,
  periodLabel,
} from "@/lib/planCopy";

/**
 * Compact usage indicator for the top bar. Entitlement-aware (2026 hard-trial):
 *
 *   - FREE tier  → NO daily/monthly allowance. It's a one-time trial: show the
 *                  single free Find-Customers scan (1 left → used). The old
 *                  "X/3 today" daily counter does NOT apply to free users.
 *   - PAID (monthly) → the monthly scan allowance (balance / cap).
 *   - Legacy daily-cap fallback (grandfathered edge cases) → below.
 */
export default function UsageBadge({ usage, entitlements, status = "ready", testId }) {
  const ent = entitlements || {};

  // ── Workspace not resolved: say NOTHING about it (§4) ─────────────────────
  // This slot used to fall through to "--/-- today", which is worse than blank
  // on two counts: it states a daily-cap model that free users are not even on,
  // and it looks like a real reading of an empty account rather than an absent
  // one. The badge is a quiet slot; the banner owns the message.
  if (!entitlements && status !== "ready") {
    const stalled = status === "retrying" || status === "unreachable";
    return (
      <div
        data-testid={testId}
        data-ws-slot={stalled ? "stalled" : status}
        aria-hidden="true"
        className={`inline-flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-mono min-w-[64px] border ${
          status === "loading"
            ? "ws-shimmer border-lm-border"
            : "bg-lm-tint border-lm-border text-lm-text3"
        }`}
      >
        {stalled ? "—" : " "}
      </div>
    );
  }

  // ── FREE hard-trial tier — one free scan, never refilled ──────────────────
  if (ent.is_paid === false) {
    const hasScan = !!ent.can_scan;
    return (
      <div
        data-testid={testId}
        title={
          hasScan
            ? `${freeTrialBlurb()} Upgrade to open sources, save leads, and get ${starterScansPhrase()}.`
            : freeScanUsedPhrase()
        }
        className={`inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full text-sm font-mono font-semibold border ${
          hasScan
            ? "bg-amber-50 text-amber-700 border-amber-200"
            : "bg-lm-tint text-lm-text3 border-lm-border"
        }`}
      >
        {hasScan ? (
          <Gift className="w-3.5 h-3.5" strokeWidth={2.2} />
        ) : (
          <Lock className="w-3.5 h-3.5" strokeWidth={2.2} />
        )}
        {/* The trial size comes from planCopy, not a literal "1" — if the free
            allowance ever changes this follows it instead of quietly lying. */}
        <span>{hasScan ? String(planNumbers().freeScans) : "0"}</span>
        <span className="hidden sm:inline text-lm-text3 font-sans font-normal">
          {hasScan ? "free scan" : "scan used"}
        </span>
      </div>
    );
  }

  if (!usage) {
    return (
      <div
        data-testid={testId}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lm-tint text-lm-text3 text-sm font-mono"
      >
        {/* No usage snapshot means we don't know the period either — "today"
            here asserted a daily model for a paid user whose plan is monthly. */}
        --/--
      </div>
    );
  }

  // ── PAID monthly allowance ────────────────────────────────────────────────
  if (usage.quota_model === "monthly") {
    const cap = Number(usage.allowance_cap ?? 0);
    const balance = Number(usage.allowance_balance ?? 0);
    const used = Math.max(0, cap - balance);
    const nearFull = cap > 0 && used / Math.max(cap, 1) >= 0.8;
    return (
      <div
        data-testid={testId}
        title={`${used} of ${cap} scans used this month`}
        className={`inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full text-sm font-mono font-semibold border ${
          nearFull
            ? "bg-lm-tint3 text-lm-secondary border-lm-secondary/25"
            : "bg-lm-tint text-lm-text2 border-lm-border"
        }`}
      >
        <Zap className="w-3.5 h-3.5" strokeWidth={2.2} />
        <span>
          {balance}
          <span className="opacity-50 mx-0.5">/</span>
          {cap}
        </span>
        <span className="hidden sm:inline text-lm-text3 font-sans font-normal">this month</span>
      </div>
    );
  }

  // ── Daily-cap branch (free tier, and grandfathered edge cases) ────────────
  //
  // Reached whenever `quota_model` is not the literal "monthly" — INCLUDING
  // when it is undefined. It previously asserted "today" and "resets at IST
  // midnight" in that unknown case, so a paid user served a snapshot without
  // `quota_model` was told their monthly allowance reset overnight. The period
  // word now comes from the server's model and is omitted when it is unknown.
  const dailyUsed = usage.account_used ?? 0;
  const dailyLimit = usage.account_limit ?? 0;
  const remaining = Math.max(0, dailyLimit - dailyUsed);
  const nearFull = dailyLimit > 0 && dailyUsed / Math.max(dailyLimit, 1) >= 0.8;
  const period = periodLabel(usage); // "today" | "this month" | null
  const resetNote = usage.quota_model === "daily" ? " (resets at IST midnight)" : "";

  return (
    <div
      data-testid={testId}
      title={`${dailyUsed} of ${dailyLimit} scans used${period ? ` ${period}` : ""}${resetNote}`}
      className={`inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full text-sm font-mono font-semibold border ${
        nearFull
          ? "bg-lm-tint3 text-lm-secondary border-lm-secondary/25"
          : "bg-lm-tint text-lm-text2 border-lm-border"
      }`}
    >
      <Zap className="w-3.5 h-3.5" strokeWidth={2.2} />
      <span>
        {remaining}
        <span className="opacity-50 mx-0.5">/</span>
        {dailyLimit}
      </span>
      {period && (
        <span className="hidden sm:inline text-lm-text3 font-sans font-normal">{period}</span>
      )}
    </div>
  );
}
